import React, { useState } from "react";
import { XMarkIcon as X } from "@heroicons/react/24/outline";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import NewsletterForm from "../ui/NewsletterForm";
import "./NewsletterBanner.css";

interface NewsletterBannerProps {
  onClose?: () => void;
  showCloseButton?: boolean;
  variant?: "banner" | "modal" | "inline";
}

const NewsletterBanner: React.FC<NewsletterBannerProps> = ({
  onClose,
  showCloseButton = true,
  variant = "banner",
}) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(true);

  const handleClose = () => {
    setIsVisible(false);
    if (onClose) {
      onClose();
    }
  };

  const bannerVariants = {
    hidden: { opacity: 0, y: -100 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
      },
    },
    exit: {
      opacity: 0,
      y: -100,
      transition: {
        duration: 0.3,
      },
    },
  };

  const bannerContent = (
    <div className="relative max-w-md w-full bg-white rounded-2xl shadow-xl overflow-hidden">
      {/* Close Button - Top Right */}
      {showCloseButton && (
        <button
          onClick={handleClose}
          className="absolute top-2 right-2 p-1 bg-white/80 hover:bg-gray-100 rounded-full transition-colors duration-200 z-20 shadow-sm border border-gray-200"
          aria-label={t("common.close", "Cerrar")}
          title={t("common.close", "Cerrar")}
        >
          <X className="h-4 w-4 text-gray-600" />
        </button>
      )}

      <div className="bg-gradient-to-r from-[#00e9be] to-[#005a50] p-6 text-white text-center">
        <h3 className="text-2xl font-bold mb-2">
          {t("newsletter.formTitle", "Suscribe vettINSIDER!!")}
        </h3>
        <p className="text-white/90 text-sm">
          {t("newsletter.subtitle", "Join our weekly newsletter for the latest veterinary news, expert analysis, and actionable tips delivered straight to your inbox.")}
        </p>
      </div>

      <div className="p-6">
        <NewsletterForm 
            variant="full" 
            onSuccess={() => {
                setTimeout(() => {
                    handleClose();
                }, 2000);
            }}
        />
      </div>
    </div>
  );

  if (variant === "banner") {
    return (
      <AnimatePresence>
        {isVisible && (
          <motion.div
            variants={bannerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="fixed top-0 left-0 right-0 z-50 flex justify-center p-4 pointer-events-none"
          >
            <div className="pointer-events-auto shadow-2xl rounded-2xl">
                {bannerContent}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  if (variant === "modal") {
    return (
      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={handleClose}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="max-w-md w-full rounded-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {bannerContent}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  // Inline variant
  return <div className="w-full">{bannerContent}</div>;
};

export default NewsletterBanner;
